import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppDownloadLinks from './AppDownloadLinks';

export default function Footer() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  return (
    <footer className="bg-slate-950 border-t border-white/10 mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-2 mb-3">
              <img src="/logo.png" alt="BuildMyShifts" className="w-8 h-8 object-contain" />
              <h3 className="text-lg font-bold text-white">
                Build My <span className="text-primary">Shifts</span>
              </h3>
            </div>
            <p className="text-sm text-gray-300 leading-relaxed">
              {t('app.description')}
            </p>
            <p className="mt-2 text-sm font-semibold text-primary">
              {t('app.tagline')}
            </p>
            {/* Socials */}
            <div className="flex items-center gap-3 mt-4">
              <a href="https://x.com/BuildMyShifts" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="X (Twitter)">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
              <a href="https://www.facebook.com/profile.php?id=61573480784830" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Facebook">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://www.instagram.com/buildmyshifts/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Instagram">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2zm0 1.5A4.25 4.25 0 0 0 3.5 7.75v8.5a4.25 4.25 0 0 0 4.25 4.25h8.5a4.25 4.25 0 0 0 4.25-4.25v-8.5a4.25 4.25 0 0 0-4.25-4.25h-8.5zm8.88 1.87a1.12 1.12 0 1 1 0 2.24 1.12 1.12 0 0 1 0-2.24zM12 6.5A5.5 5.5 0 1 1 6.5 12 5.5 5.5 0 0 1 12 6.5zm0 1.5A4 4 0 1 0 16 12a4 4 0 0 0-4-4z"/></svg>
              </a>
              <a href="https://www.youtube.com/@BuildMyShifts" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="YouTube">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              </a>
              <a href="https://www.linkedin.com/company/buildmyshifts1" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="LinkedIn">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
            </div>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-semibold text-white mb-3">{t('footer.product', 'Product')}</h4>
            <ul className="space-y-2 text-sm">
              <li><button onClick={() => navigate('/features')} className="text-gray-300 hover:text-primary transition-colors">{t('footer.features', 'Features')}</button></li>
              <li><button onClick={() => navigate('/pricing')} className="text-gray-300 hover:text-primary transition-colors">{t('footer.pricing', 'Pricing')}</button></li>
              <li><button onClick={() => navigate('/contact')} className="text-gray-300 hover:text-primary transition-colors">{t('footer.contact', 'Contact')}</button></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold text-white mb-3">{t('footer.legal', 'Legal')}</h4>
            <ul className="space-y-2 text-sm">
              <li><button onClick={() => navigate('/terms')} className="text-gray-300 hover:text-primary transition-colors">{t('footer.termsOfService', 'Terms of Service')}</button></li>
              <li><button onClick={() => navigate('/privacy')} className="text-gray-300 hover:text-primary transition-colors">{t('footer.privacyPolicy', 'Privacy Policy')}</button></li>
              <li><button onClick={() => navigate('/refund-policy')} className="text-gray-300 hover:text-primary transition-colors">{t('footer.refundPolicy', 'Refund Policy')}</button></li>
              <li><button onClick={() => navigate('/delete-account')} className="text-gray-300 hover:text-primary transition-colors">{t('footer.deleteAccount', 'Delete Account')}</button></li>
            </ul>
          </div>

          {/* Sister Product */}
          <div>
            <h4 className="font-semibold text-white mb-3">{t('footer.otherProducts', 'Our Other Products')}</h4>
            <a
              href="https://buildmychecks.com"
              target="_blank"
              rel="noopener noreferrer"
              className="group block rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 p-4 transition-all"
            >
              <div className="flex items-center gap-3 mb-2">
                <img src="https://buildmychecks.com/logo192.png" alt="BuildMyChecks" className="w-8 h-8 rounded-lg" />
                <span className="font-semibold text-white group-hover:text-primary transition-colors">BuildMyChecks</span>
              </div>
              <p className="text-xs text-gray-400 leading-relaxed">
                {t('footer.bmcDescription', 'Digital safety checks, compliance forms & asset management. Save 50% when you bundle with BuildMyShifts!')}
              </p>
              <span className="inline-block mt-2 text-xs font-medium text-primary">
                buildmychecks.com &rarr;
              </span>
            </a>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-white/15">
          <AppDownloadLinks dark className="max-w-2xl" />
        </div>

        <div className="mt-8 pt-6 border-t border-white/15 flex flex-col sm:flex-row justify-between items-center text-sm text-gray-400">
          <p>&copy; {year} {t('footer.copyright', 'BuildMyShifts.com. All rights reserved.')}</p>
          <div className="flex items-center gap-4 mt-2 sm:mt-0">
            <a href="https://buildmychecks.com" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">BuildMyChecks</a>
            <span>•</span>
            <p>{t('footer.partOf', 'Part of the')} <span className="font-semibold text-primary">BuildMy</span> {t('footer.platform', 'platform')}</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
